import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useCropOptions } from '@features/crops/hooks';
import { useVarietyOptions } from '@features/varieties/hooks';
import { useApmcOptions } from '@features/apmcs/hooks';
import { listPriceSeries } from '@features/priceSeries/api';

interface Props {
  /** Selected price_series id (or null while unmapped). */
  value: string | null;
  onChange: (seriesId: string | null) => void;
  /** Raw pipeline text shown as placeholders to guide the mapping. */
  cropText: string | null;
  varietyText: string | null;
  apmcText: string | null;
}

/**
 * Maps an unresolved staging row to a master `price_series`. The reviewer picks
 * crop → variety → mandi; the (apmc, crop, variety) tuple is unique, so we look
 * up the single matching series and surface its id (feeding the approve body).
 * When no series exists for the combo, we say so — it must be created in Price
 * series first (out of scope here).
 */
export function SeriesPicker({
  value,
  onChange,
  cropText,
  varietyText,
  apmcText,
}: Props) {
  const [cropId, setCropId] = useState<string | null>(null);
  const [varietyId, setVarietyId] = useState<string | null>(null);
  const [apmcId, setApmcId] = useState<string | null>(null);

  const cropOptions = useCropOptions();
  const varietyOptions = useVarietyOptions();
  const apmcOptions = useApmcOptions();

  const crops = useMemo(() => cropOptions.data ?? [], [cropOptions.data]);
  const apmcs = useMemo(() => apmcOptions.data ?? [], [apmcOptions.data]);
  const varietiesForCrop = useMemo(
    () =>
      cropId == null
        ? []
        : (varietyOptions.data ?? []).filter((v) => v.cropId === cropId),
    [varietyOptions.data, cropId],
  );

  const allChosen = cropId != null && varietyId != null && apmcId != null;

  const seriesQuery = useQuery({
    queryKey: ['priceReview', 'seriesLookup', cropId, varietyId, apmcId],
    queryFn: () =>
      listPriceSeries({
        page: 1,
        pageSize: 1,
        cropId: Number(cropId),
        varietyId: Number(varietyId),
        apmcId: Number(apmcId),
      }),
    enabled: allChosen,
  });

  // Push the resolved series id (or null) up whenever the lookup settles.
  const resolvedId = allChosen
    ? (seriesQuery.data?.rowData[0]?.id ?? null)
    : null;
  useEffect(() => {
    if (!allChosen) {
      if (value !== null) onChange(null);
      return;
    }
    if (seriesQuery.isFetching) return;
    if (resolvedId !== value) onChange(resolvedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allChosen, resolvedId, seriesQuery.isFetching]);

  const noMatch = allChosen && !seriesQuery.isFetching && resolvedId == null;

  const cropValue = crops.find((c) => c.id === cropId) ?? null;
  const varietyValue = varietiesForCrop.find((v) => v.id === varietyId) ?? null;
  const apmcValue = apmcs.find((a) => a.id === apmcId) ?? null;

  return (
    <div className="rounded-lg border border-warning/[0.4] bg-warning/[0.06] p-3">
      <div className="mb-2 text-[12px] font-semibold text-warning">
        Map to a price series before approving
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Autocomplete
          size="small"
          options={crops}
          getOptionLabel={(o) => o.name}
          value={cropValue}
          loading={cropOptions.isLoading}
          onChange={(_, o) => {
            setCropId(o?.id ?? null);
            setVarietyId(null);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Crop"
              placeholder={cropText ?? undefined}
            />
          )}
        />
        <Autocomplete
          size="small"
          options={varietiesForCrop}
          getOptionLabel={(o) => o.name}
          value={varietyValue}
          disabled={cropId == null}
          onChange={(_, o) => setVarietyId(o?.id ?? null)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Variety"
              placeholder={varietyText ?? undefined}
            />
          )}
        />
        <Autocomplete
          size="small"
          options={apmcs}
          getOptionLabel={(o) => o.name}
          value={apmcValue}
          loading={apmcOptions.isLoading}
          onChange={(_, o) => setApmcId(o?.id ?? null)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Mandi"
              placeholder={apmcText ?? undefined}
            />
          )}
        />
      </div>
      <div className="mt-2 min-h-[20px] text-[12px]">
        {seriesQuery.isFetching && allChosen && (
          <span className="inline-flex items-center gap-1.5 text-foreground-muted">
            <CircularProgress size={12} /> Resolving series…
          </span>
        )}
        {noMatch && (
          <span className="text-error">
            No price series exists for this crop / variety / mandi. Create it in
            Price series first.
          </span>
        )}
        {resolvedId && (
          <span className="text-success">
            Matched price series #{resolvedId}.
          </span>
        )}
      </div>
    </div>
  );
}
